const delayTimer = (time: number) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true)
    }, time)
  })
}

/**
 * 方法锁
 * 在上一次方法完成前，中断本次方法调用
 * const fnLock = decoratorLock(fn)
 */
export type DecoratorLockOptions = {
  lockedHandle?: Function,
  unLockedHandle?: Function,
  lockTime?: number,
}
export type DecoratorLock = <T extends (...args: any[]) => any>(
  fn: T,
  options?: DecoratorLockOptions
) => (...args: Parameters<T>) => Promise<void>
export const decoratorLock: DecoratorLock = (fn, options) => {
  const { lockedHandle, unLockedHandle, lockTime } = options || {}
  let isLocked = false
  return async (...args: any[]) => {
    if (isLocked) {
      if (lockedHandle) {
        lockedHandle?.()
      }
      return
    }
    isLocked = true
    try {
      await fn(...args)
    } catch (error) {
      console.error(error)
    }
    if (lockTime) {
      await delayTimer(lockTime)
    }
    isLocked = false
    if (unLockedHandle) {
      unLockedHandle?.()
    }
  }
}

/**
 * 防抖
 */
export type DecoratorDebounceOptions = {
  delayTime?: number,
}
export type DecoratorDebounce = <T extends (...args: any[]) => any>(
  fn: T,
  options?: DecoratorDebounceOptions
) => (...args: Parameters<T>) => void
export const decoratorDebounce: DecoratorDebounce = (fn, options) => {
  const { delayTime } = options || {}
  let timer: NodeJS.Timeout | null = null
  return (...args: any[]) => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      fn(...args)
    }, delayTime)
  }
}

/**
 * 轮询至成功
 */
export type DecoratorPollingOptions = {
  interval?: number, // 轮询间隔时间
  maxCount?: number, // 最大轮询次数
}
export type DecoratorPolling = <T extends (...args: any[]) => any>(
  fn: T,
  options?: DecoratorPollingOptions
) => [Function, Function]
export const decoratorPolling: DecoratorPolling = (fn, options) => {
  const { 
    interval = 1000, 
    maxCount = 5,
  } = options || {}
  let times = 0

  let error: Error | null = null

  // 手动停止轮询
  const stopPolling = () => {
    times = maxCount + 1
  }

  const handlePolling = async (...args: any[]) => {
    if (times > maxCount) {
      throw {
        code: 'STOP_POLLING',
        message: '手动停止轮询',
      }
    }

    try {
      const res = await fn(...args)
      if (res) {
        return res
      }
      error = null
    } catch (error) {
      error = error
    }

    times++

    if (times <= maxCount) {
      await delayTimer(interval)
      return handlePolling(...args)
    }
    
    if (error && error) {
      console.error('轮询失败', error)
      throw error
    }

    return null
  }
  return [handlePolling, stopPolling]
}



/**
 * 只执行一次
 */ 
export type DecoratorOnce = <T extends (...args: any[]) => any>(
  fn: T
) => (...args: Parameters<T>) => Promise<void>
export const decoratorOnce: DecoratorOnce = (fn) => {
  let isCalled = false
  return async (...args: any[]) => {
    if (isCalled) {
      console.log('[decoratorOnce] 已执行过，跳过')
      return
    }
    console.log('[decoratorOnce] 执行')
    isCalled = true
    try {
      await fn(...args)
    } catch (error) {
      console.error(error)
    }
  }
}

/**
 * 异步方法节流
 * 1. 在缓存有效期内缓存结果
 * 2. 当同时发起多个同样的异步方法时，只执行一次，且返回同一个结果
 * 3. 在缓存有效期外，重新执行异步方法
 */
export type DecoratorAsyncCache = <T extends (...args: any[]) => any>(
  fn: T,
  cacheTime?: number
) => (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>>
export const decoratorAsyncCache = <T extends (...args: any[]) => any>(fn: T, cacheTime: number = 0) => {
  let isCalled = false
  let cacheRes: Awaited<ReturnType<T>> | null = null
  let cacheExpireTime = Date.now() + cacheTime 
  let callbackFnList: Array<{
    resolve: (value: Awaited<ReturnType<T>>) => void
    reject: (reason?: any) => void
  }> = []

  const trigger = async (...args: any[]): Promise<Awaited<ReturnType<T>>> => {
    // 如果在缓存有效期内，直接返回缓存结果
    if (cacheExpireTime > Date.now() && cacheRes !== null) {
      return cacheRes
    }

    // 如果正在执行，则将当前调用加入等待队列
    if (isCalled) {
      return new Promise<Awaited<ReturnType<T>>>((resolve, reject) => {
        callbackFnList.push({ resolve, reject })
      })
    }

    isCalled = true

    try {
      cacheRes = await fn(...args) // 执行异步方法
      cacheExpireTime = Date.now() + cacheTime // 更新缓存过期时间

      // 将结果返回给所有等待的调用者
      callbackFnList.forEach(({ resolve }) => {
        resolve(cacheRes as Awaited<ReturnType<T>>)
      })
      callbackFnList = []
      
      return cacheRes as Awaited<ReturnType<T>>
    } catch (error) {
      // 如果执行失败，将错误传递给所有等待的调用者
      callbackFnList.forEach(({ reject }) => {
        reject(error)
      })
      callbackFnList = []
      
      throw error
    } finally {
      isCalled = false
    }
  }

  return trigger
}
